import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Save, Send, ExternalLink, Trash2, Upload, Music, MessageCircle } from 'lucide-react';
import RichTextArea from '@/components/RichTextArea';
import PageControlToggle from './CompetitionPages/PageControlToggle';

import { toast } from 'sonner';

export default function CompetitionPageEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [competitions, setCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [competitionId, setCompetitionId] = useState('');
  const [pageTitle, setPageTitle] = useState('');
  const [openingParagraph, setOpeningParagraph] = useState('');
  const [uploadsEnabled, setUploadsEnabled] = useState(true);
  const [savingUploadsToggle, setSavingUploadsToggle] = useState(false);
  const [musicSubmissionsEnabled, setMusicSubmissionsEnabled] = useState(true);
  const [savingMusicToggle, setSavingMusicToggle] = useState(false);
  const [inquiryFormEnabled, setInquiryFormEnabled] = useState(true);
  const [savingInquiryToggle, setSavingInquiryToggle] = useState(false);
  const [status, setStatus] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  
  // Fetch current user
  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => setCurrentUser(res.data))
      .catch(err => console.error('Failed to fetch user:', err));
  }, []);
  
  // Fetch competitions and clubs on load
  useEffect(() => {
    axios.get(`${API}/competitions`, { withCredentials: true })
      .then(res => {
        setCompetitions(res.data);
        // Auto-select if only one competition available and not editing existing page
        if (!id && res.data.length === 1) {
          const comp = res.data[0];
          setCompetitionId(comp.competition_id);
          setSelectedCompetition(comp);
          // Set default title: "The YYYY <<Competition Name>> is on the way!"
          const year = comp.start_date ? new Date(comp.start_date).getFullYear() : new Date().getFullYear();
          setPageTitle(`The ${year} ${comp.name} is on the way!`);
          // Fetch host organisation for default intro
          fetchHostAndSetDefaults(comp);
        }
      })
      .catch(err => console.error(err));
  }, [id]);
  
  // Fetch host organisation and set default opening paragraph
  const fetchHostAndSetDefaults = async (comp) => {
    if (comp.host_organisation_id) {
      try {
        const res = await axios.get(`${API}/clubs`, { withCredentials: true });
        const host = res.data.find(c => c.club_id === comp.host_organisation_id);
        if (host) {
          const year = comp.start_date ? new Date(comp.start_date).getFullYear() : new Date().getFullYear();
          // Default intro: "The <<Host Name>> present the YYYY <<Competition Name>>."
          setOpeningParagraph(`The ${host.name} present the ${year} ${comp.name}.`);
        }
      } catch (err) {
        console.error('Could not fetch host organisation');
      }
    }
  };
  
  // Fetch existing page data if editing
  useEffect(() => {
    if (id) {
      axios.get(`${API}/competition-pages`, { withCredentials: true })
        .then(res => {
          const page = res.data.find(p => p.page_id === id);
          if (page) {
            setCompetitionId(page.competition_id);
            setPageTitle(page.title || '');
            // Load HTML content as-is into the rich-text editor.
            setOpeningParagraph(page.content || '');
            setUploadsEnabled(page.uploads_enabled !== false);
            setMusicSubmissionsEnabled(page.music_submissions_enabled !== false);
            setInquiryFormEnabled(page.inquiry_form_enabled !== false);
            setStatus(page.status);
            
            // Find the competition
            const comp = competitions.find(c => c.competition_id === page.competition_id);
            if (comp) setSelectedCompetition(comp);
          }
        })
        .catch(err => console.error(err));
    }
  }, [id, competitions]);
  
  // Update default values when competition is selected
  const handleCompetitionChange = (compId) => {
    setCompetitionId(compId);
    const comp = competitions.find(c => c.competition_id === compId);
    setSelectedCompetition(comp);
    
    if (comp) {
      // Set default title: "The YYYY <<Competition Name>> is on the way!"
      const year = comp.start_date ? new Date(comp.start_date).getFullYear() : new Date().getFullYear();
      setPageTitle(`The ${year} ${comp.name} is on the way!`);
      
      // Set default opening paragraph with host organisation
      fetchHostAndSetDefaults(comp);
    }
  };
  
  const handleUploadsEnabledChange = async (next) => {
    if (!id) {
      // No saved page yet → just update local state; will persist on first Save.
      setUploadsEnabled(next);
      return;
    }
    setSavingUploadsToggle(true);
    const prev = uploadsEnabled;
    setUploadsEnabled(next);  // optimistic
    try {
      await axios.patch(
        `${API}/competition-pages/${id}/uploads-enabled`,
        { uploads_enabled: next },
        { withCredentials: true }
      );
      toast.success(
        next
          ? 'Upload Entries control enabled on the live page'
          : 'Upload Entries control disabled — live page now shows "Entries are now closed"'
      );
    } catch (error) {
      setUploadsEnabled(prev);
      toast.error('Failed to update Upload Entries control');
    } finally {
      setSavingUploadsToggle(false);
    }
  };

  const handleMusicSubmissionsEnabledChange = async (next) => {
    if (!id) {
      setMusicSubmissionsEnabled(next);
      return;
    }
    setSavingMusicToggle(true);
    const prev = musicSubmissionsEnabled;
    setMusicSubmissionsEnabled(next);  // optimistic
    try {
      await axios.patch(
        `${API}/competition-pages/${id}/music-submissions-enabled`,
        { music_submissions_enabled: next },
        { withCredentials: true }
      );
      toast.success(
        next
          ? 'Music and Content Sheet Submission control enabled on the live page'
          : 'Music and Content Sheet Submission control disabled — live page now shows the closed notice'
      );
    } catch (error) {
      setMusicSubmissionsEnabled(prev);
      toast.error('Failed to update Music and Content Sheet Submission control');
    } finally {
      setSavingMusicToggle(false);
    }
  };

  const handleInquiryFormEnabledChange = async (next) => {
    if (!id) {
      setInquiryFormEnabled(next);
      return;
    }
    setSavingInquiryToggle(true);
    const prev = inquiryFormEnabled;
    setInquiryFormEnabled(next);  // optimistic
    try {
      await axios.patch(
        `${API}/competition-pages/${id}/inquiry-form-enabled`,
        { inquiry_form_enabled: next },
        { withCredentials: true }
      );
      toast.success(
        next
          ? 'Questions or Comments form enabled on the live page'
          : 'Questions or Comments form hidden — contact email link still visible on the live page'
      );
    } catch (error) {
      setInquiryFormEnabled(prev);
      toast.error('Failed to update Questions or Comments form control');
    } finally {
      setSavingInquiryToggle(false);
    }
  };

  const handleSave = async () => {
    if (!competitionId) {
      toast.error('Please select a competition');
      return;
    }
    
    try {
      // Editor already emits HTML (with its own <p> wrappers); persist
      // it verbatim. An empty editor stores an empty string so the
      // public page falls back to the auto-generated intro.
      const wrappedContent = openingParagraph || '';
      
      if (id) {
        const previousStatus = status;
        const response = await axios.patch(`${API}/competition-pages/${id}`, {
          title: pageTitle,
          content: wrappedContent
        }, { withCredentials: true });
        
        // Check if status changed (Standard User editing approved page)
        if (response.data.status !== previousStatus) {
          setStatus(response.data.status);
          if (response.data.status === 'pending') {
            toast.success('Page updated and submitted for re-approval');
          } else {
            toast.success('Page updated!');
          }
        } else {
          toast.success('Page updated!');
        }
      } else {
        const response = await axios.post(`${API}/competition-pages`, {
          competition_id: competitionId,
          title: pageTitle,
          content: wrappedContent,
          show_schedule: true,
          show_participants: true,
          show_results: false,
          uploads_enabled: uploadsEnabled,
          music_submissions_enabled: musicSubmissionsEnabled,
          inquiry_form_enabled: inquiryFormEnabled
        }, { withCredentials: true });
        
        // Check if Super User (auto-approved)
        if (response.data.status === 'approved') {
          toast.success('Page created and published!');
        } else {
          toast.success('Page created!');
        }
        // Navigate to edit the new page
        navigate(`/competition-pages/${response.data.page_id}`);
      }
    } catch (error) {
      toast.error('Failed to save page');
    }
  };
  
  const handleSubmit = async () => {
    if (!id) {
      toast.error('Please save the page first');
      return;
    }
    try {
      await axios.post(`${API}/competition-pages/${id}/submit`, {}, { withCredentials: true });
      toast.success('Submitted for approval!');
      setStatus('pending');
    } catch (error) {
      toast.error('Failed to submit for approval');
    }
  };

  // Super-user inline approval banner (shown when status === 'pending').
  // Same rules as /admin/approvals: comment optional to approve,
  // required to reject.
  const [reviewComment, setReviewComment] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);

  const performReviewAction = async (action) => {
    if (action === 'rejected' && !reviewComment.trim()) {
      toast.error('Please add a reason before rejecting');
      return;
    }
    setReviewBusy(true);
    try {
      // Fetch the pending approval_id for this page (super-users only).
      const approvalsRes = await axios.get(`${API}/approvals`, { withCredentials: true });
      const match = (approvalsRes.data || []).find(
        (a) => a.page_id === id && a.status === 'pending'
      );
      if (!match) {
        toast.error('No pending approval request found for this page');
        setReviewBusy(false);
        return;
      }
      await axios.post(`${API}/approvals/${match.approval_id}/comment`, {
        comment: reviewComment.trim() || 'Approved',
        action,
      }, { withCredentials: true });
      toast.success(action === 'approved' ? 'Page approved' : 'Page rejected');
      setReviewComment('');
      // Mirror the server-side status update locally so the banner
      // hides and the status badge repaints without a full reload.
      setStatus(action === 'approved' ? 'approved' : 'rejected');
    } catch (error) {
      toast.error('Failed to process review');
    } finally {
      setReviewBusy(false);
    }
  };
  
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/competition-pages/${id}`, { withCredentials: true });
      toast.success('Page deleted');
      navigate('/competition-pages');
    } catch (error) {
      toast.error('Failed to delete page');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };
  
  const getStatusBadge = () => {
    const colors = {
      'draft': 'bg-gray-100 text-gray-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${colors[status] || colors.draft}`}>
        {status || 'draft'}
      </span>
    );
  };
  
  // Get live page URL
  const getLivePageUrl = () => {
    if (id && status === 'approved') {
      return `${window.location.origin}/competition/${id}`;
    }
    return null;
  };
  
  return (
    <div className="space-y-6 fade-in max-w-4xl">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/competition-pages')} data-testid="back-button">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Pages
        </Button>
        
        {id && status === 'approved' && (
          <a 
            href={getLivePageUrl()} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-green-700 hover:text-green-800 font-medium"
          >
            <ExternalLink className="h-4 w-4" />
            View Live Page
          </a>
        )}
      </div>
      
      <Card className="p-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
            {id ? 'Edit Competition Page' : 'Create Competition Page'}
          </h1>
          {status && getStatusBadge()}
        </div>

        {/* Super-user inline approval banner — surfaces the action
            right here on the editor so a super user viewing a pending
            page doesn't have to navigate away to approve it. */}
        {id && status === 'pending' && currentUser?.role === 'super' && (
          <div
            className="mb-6 p-4 rounded-lg border border-amber-200 bg-amber-50"
            data-testid="editor-approval-banner"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="flex-1">
                <div className="text-sm font-semibold text-amber-800">
                  This page is awaiting your review
                </div>
                <div className="text-xs text-amber-700 mt-0.5">
                  Approve to publish it, reject to send it back with feedback. On reject, your review notes are emailed to the submitter as the required changes, so be specific.
                </div>
              </div>
              <Link
                to={`/admin/preview/${id}`}
                className="text-sm text-amber-700 hover:text-amber-900 underline whitespace-nowrap"
                data-testid="editor-approval-preview-link"
              >
                Open preview →
              </Link>
            </div>
            <Input
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              placeholder="On reject, list the specific changes the submitter needs to make (emailed to them verbatim)…"
              className="mb-3 bg-white"
              data-testid="editor-approval-comment"
            />
            <div className="flex gap-2">
              <Button
                onClick={() => performReviewAction('approved')}
                disabled={reviewBusy}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="editor-approve-btn"
              >
                {reviewBusy ? 'Working…' : 'Approve'}
              </Button>
              <Button
                onClick={() => performReviewAction('rejected')}
                disabled={reviewBusy}
                variant="destructive"
                data-testid="editor-reject-btn"
              >
                Reject
              </Button>
            </div>
          </div>
        )}
        
        <div className="space-y-6">
          {/* Competition Selection */}
          <div>
            <Label>Competition *</Label>
            <select
              className="w-full border rounded-lg px-3 py-2 mt-1"
              value={competitionId}
              onChange={(e) => handleCompetitionChange(e.target.value)}
              data-testid="competition-select"
              disabled={!!id}
            >
              <option value="">Select Competition</option>
              {competitions.map(c => (
                <option key={c.competition_id} value={c.competition_id}>{c.name}</option>
              ))}
            </select>
          </div>
          
          {/* Page Header Section */}
          <div className="p-4 border rounded-lg bg-stone-50">
            <h3 className="font-semibold text-stone-800 mb-4">Page Header</h3>
            
            <div className="space-y-4">
              <div>
                <Label>Page Title</Label>
                <Input 
                  value={pageTitle} 
                  onChange={(e) => setPageTitle(e.target.value)} 
                  data-testid="page-title-input" 
                  className="mt-1"
                  placeholder="e.g. The 2026 Auckland Regional Championships is on the way!"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Default: "The [Year] [Competition Name] is on the way!" - customise if needed
                </p>
              </div>
              
              <div>
                <Label>Opening Paragraph</Label>
                <RichTextArea
                  value={openingParagraph}
                  onChange={setOpeningParagraph}
                  testId="opening-paragraph-input"
                  className="mt-1"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Default: "The [Host Name] present the [Year] [Competition Name]." - customise if needed
                </p>
              </div>
            </div>
          </div>
          
          {/* Included Elements Info */}
          <div className="p-4 border rounded-lg bg-green-50">
            <h3 className="font-semibold text-green-800 mb-3">Page Elements (Included by Default)</h3>
            <ul className="space-y-2 text-sm text-green-700">
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <strong>Contact</strong> - Host organisation name and email
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <strong>When and Where</strong> - Venue details, dates, and registration deadline
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <strong>Competition Documents</strong> - Published documents (shown only if documents exist)
              </li>
              <li className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <strong>Questions/Comments Form</strong> - Contact form for inquiries
              </li>
            </ul>
          </div>

          {/* Upload Entries Control */}
          <PageControlToggle
            icon={Upload}
            header="Upload Entries"
            tagline="Entries can be uploaded via this control on the Competition Page using the Entry Summary Worksheet."
            checked={uploadsEnabled}
            onChange={handleUploadsEnabledChange}
            disabled={savingUploadsToggle}
            testId="uploads-enabled-toggle"
            tooltipTitle="Toggle the live Upload Entries control on the public Competition Page"
            pageId={id}
            controlKind="uploads_enabled"
          />

          {/* Music and Content Sheet Submission Control */}
          <PageControlToggle
            icon={Music}
            header="Music and Content Sheet Submission"
            tagline="Music and content sheets can be uploaded via this control on the Competition Page."
            checked={musicSubmissionsEnabled}
            onChange={handleMusicSubmissionsEnabledChange}
            disabled={savingMusicToggle}
            testId="music-submissions-enabled-toggle"
            tooltipTitle="Toggle the live Music and Content Sheet Submission control on the public Competition Page"
            pageId={id}
            controlKind="music_submissions_enabled"
          />

          {/* Questions or Comments Form Control */}
          <PageControlToggle
            icon={MessageCircle}
            header="Questions or Comments Form"
            tagline="Allows email contact directly from the live Competition Page on the CompCreator website."
            checked={inquiryFormEnabled}
            onChange={handleInquiryFormEnabledChange}
            disabled={savingInquiryToggle}
            testId="inquiry-form-enabled-toggle"
            tooltipTitle="Toggle the live Questions or Comments form on the public Competition Page"
            pageId={id}
            controlKind="inquiry_form_enabled"
          />

          {/* Status Info */}
          {status && (
            <div className={`p-4 rounded-lg ${
              status === 'approved' ? 'bg-green-50 border border-green-200' :
              status === 'pending' ? 'bg-yellow-50 border border-yellow-200' :
              status === 'rejected' ? 'bg-red-50 border border-red-200' :
              'bg-stone-50'
            }`}>
              <p className="text-sm">
                <strong>Status:</strong> <span className="capitalize">{status}</span>
                {status === 'approved' && (
                  <span className="ml-2 text-green-700">
                    - This page is live and visible to the public
                  </span>
                )}
                {status === 'pending' && (
                  <span className="ml-2 text-yellow-700">
                    - Awaiting approval from a Super User
                  </span>
                )}
              </p>
              {/* Warning for Standard Users editing approved pages */}
              {status === 'approved' && currentUser?.role !== 'super' && (
                <p className="text-sm text-amber-700 mt-2">
                  <strong>Note:</strong> Editing this page will require re-approval before changes go live.
                </p>
              )}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-4 border-t">
            <Button onClick={handleSave} className="btn-primary" data-testid="save-page-button">
              <Save className="mr-2 h-4 w-4" />
              Save
            </Button>
            
            {id && status !== 'pending' && status !== 'approved' && (
              <Button onClick={handleSubmit} variant="outline" data-testid="submit-approval-button">
                <Send className="mr-2 h-4 w-4" />
                Submit for Approval
              </Button>
            )}
            
            {id && (
              <>
                {!showDeleteConfirm ? (
                  <Button 
                    onClick={() => setShowDeleteConfirm(true)} 
                    variant="ghost" 
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto"
                    data-testid="delete-page-button"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Page
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-sm text-red-600">Delete this page?</span>
                    <Button 
                      onClick={handleDelete} 
                      variant="destructive" 
                      size="sm"
                      disabled={deleting}
                      data-testid="confirm-delete-button"
                    >
                      {deleting ? 'Deleting...' : 'Yes, Delete'}
                    </Button>
                    <Button 
                      onClick={() => setShowDeleteConfirm(false)} 
                      variant="outline" 
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
